import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

const EMPTY_FORM = { name: '', origin: '', description: '', order: 0 };

export default function ManageCattleBreeds() {
  const [breeds, setBreeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchBreeds = () => {
    setLoading(true);
    API.get('/cattle-breeds/admin').then(r => { setBreeds(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchBreeds(); }, []);

  const resetForm = () => { setForm(EMPTY_FORM); setFile(null); setEditingId(null); };
  const openAddForm = () => { resetForm(); setShowForm(true); };
  const openEditForm = (b) => {
    setEditingId(b._id);
    setForm({ name: b.name, origin: b.origin || '', description: b.description, order: b.order || 0 });
    setFile(null);
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); resetForm(); };

  const getUrl = b => b?.image ? `${API_BASE}${b.image}` : null;

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.description.trim()) return toast.error('Description is required');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('image', file);
      if (editingId) {
        await API.put(`/cattle-breeds/${editingId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Breed updated');
      } else {
        await API.post('/cattle-breeds', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Breed added');
      }
      closeForm();
      fetchBreeds();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving');
    }
    setSaving(false);
  };

  const handleTogglePublish = async (b) => {
    try {
      await API.put(`/cattle-breeds/${b._id}`, { isActive: !b.isActive });
      toast.success(b.isActive ? 'Moved to draft' : 'Published');
      fetchBreeds();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this breed? This cannot be undone.')) return;
    try {
      await API.delete(`/cattle-breeds/${id}`);
      toast.success('Deleted');
      fetchBreeds();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 className="admin-page-title" style={{ margin: 0 }}>🐂 Manage Indian Cattle Breeds</h2>
        <button className="btn btn-primary" onClick={() => (showForm ? closeForm() : openAddForm())}>
          {showForm ? 'Cancel' : '+ Add Breed'}
        </button>
      </div>
      <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: 24 }}>
        Shown on the public "Indian Breeds" page — photo, origin, and a short description for each native breed.
      </p>

      {showForm && (
        <div className="admin-form-card" style={{ marginBottom: 24 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>{editingId ? 'Edit Breed' : 'Add Breed'}</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input className="form-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Gir" />
            </div>
            <div className="form-group">
              <label className="form-label">Origin</label>
              <input className="form-input" value={form.origin} onChange={e => setForm(p => ({ ...p, origin: e.target.value }))} placeholder="Gujarat" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description *</label>
            <textarea className="form-input" rows={4} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Photo{editingId ? ' (leave empty to keep current)' : ''}</label>
              <input type="file" accept="image/*" onChange={e => setFile(e.target.files[0])} />
            </div>
            <div className="form-group">
              <label className="form-label">Display Order</label>
              <input className="form-input" type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update Breed' : 'Save Breed'}
            </button>
            <button className="btn btn-outline" onClick={closeForm}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="loader"><div className="spinner" /></div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {breeds.map(b => (
            <div key={b._id} style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', position: 'relative', textAlign: 'center', padding: '20px 14px 14px', opacity: b.isActive ? 1 : 0.6 }}>
              <span className={`badge ${b.isActive ? 'badge-green' : 'badge-orange'}`} style={{ position: 'absolute', top: 10, left: 10 }}>
                {b.isActive ? 'Published' : 'Draft'}
              </span>
              {getUrl(b)
                ? <img src={getUrl(b)} alt={b.name} style={{ width: 90, height: 90, borderRadius: '50%', objectFit: 'cover', margin: '20px auto 10px', display: 'block' }} />
                : <div style={{ width: 90, height: 90, borderRadius: '50%', margin: '20px auto 10px', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem' }}>🐂</div>
              }
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{b.name}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--green-dark)', fontWeight: 600, marginTop: 2, marginBottom: 14 }}>{b.origin}</div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                <button title="View" onClick={() => setViewing(b)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>👁</button>
                <button title="Edit" onClick={() => openEditForm(b)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>✏️</button>
                <button title={b.isActive ? 'Move to Draft' : 'Publish'} onClick={() => handleTogglePublish(b)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>
                  {b.isActive ? '📦' : '📢'}
                </button>
                <button title="Delete" onClick={() => handleDelete(b._id)} style={{ background: 'none', border: '1px solid #f5c6c6', color: '#c62828', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>🗑</button>
              </div>
            </div>
          ))}
          {breeds.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 40, color: '#888' }}>No breeds added yet.</div>
          )}
        </div>
      )}

      {viewing && (
        <div className="modal-overlay" onClick={() => setViewing(null)}>
          <div className="modal-card auth-card" style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setViewing(null)} aria-label="Close">✕</button>
            {getUrl(viewing)
              ? <img src={getUrl(viewing)} alt={viewing.name} style={{ width: 110, height: 110, borderRadius: '50%', objectFit: 'cover', margin: '0 auto 14px' }} />
              : <div style={{ width: 110, height: 110, borderRadius: '50%', margin: '0 auto 14px', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem' }}>🐂</div>
            }
            <h3 style={{ fontWeight: 700 }}>{viewing.name}</h3>
            <div style={{ color: 'var(--green-dark)', fontWeight: 600, marginTop: 4 }}>{viewing.origin}</div>
            <span className={`badge ${viewing.isActive ? 'badge-green' : 'badge-orange'}`} style={{ display: 'inline-block', marginTop: 10 }}>
              {viewing.isActive ? 'Published' : 'Draft'}
            </span>
            {viewing.description && <p style={{ marginTop: 16, color: 'var(--text-mid)', fontSize: '0.9rem', lineHeight: 1.6, textAlign: 'left' }}>{viewing.description}</p>}
            <div style={{ marginTop: 16, fontSize: '0.8rem', color: '#999' }}>Display order: {viewing.order ?? 0}</div>
          </div>
        </div>
      )}
    </div>
  );
}
